import { Link } from 'react-router-dom';
import { ArrowRight, Droplets, Clock, Shield, Star, CheckCircle, Phone, MapPin } from 'lucide-react';

const HERO_IMAGE = "/images/hero-car.jpg";
const DETAILING_IMAGE = "/images/detailing.jpg";
const INTERIOR_IMAGE = "/images/interior.jpg";

const Home = () => {
  const highlights = [
    {
      icon: MapPin,
      title: 'Doorstep Convenience',
      description: 'We come to you – no travel, no waiting.',
    },
    {
      icon: Star,
      title: 'Premium Quality',
      description: 'Expert care using top-tier, eco-friendly products.',
    },
    {
      icon: Clock,
      title: 'Time-Saving',
      description: 'Efficient, professional service at your preferred location.',
    },
  ];

  const services = [
    {
      title: 'Premium Car Wash',
      price: 'From ₹350',
      features: ['Foam body wash', 'Interior vacuum & polish', 'Tire polish'],
      image: DETAILING_IMAGE,
    },
    {
      title: 'Quick Exterior Rinse',
      price: 'From ₹250',
      features: ['Outer body foam wash', 'No key required', 'Quick & efficient'],
      image: INTERIOR_IMAGE,
    },
    {
      title: 'VIP Membership',
      price: 'From ₹400',
      features: ['Priority service', '15-min response', 'Zero waiting time'],
      image: HERO_IMAGE,
    },
  ];

  const stats = [
    { value: '1000+', label: 'Cars Washed' },
    { value: '500+', label: 'Happy Customers' },
    { value: '4.9', label: 'Star Rating' },
    { value: '15min', label: 'Response Time' },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center bg-gradient-to-br from-blue-50 to-white pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Hero Content */}
            <div className="space-y-8">
              <div className="inline-flex items-center px-4 py-2 bg-[#1E88E5]/10 rounded-full text-[#1E88E5] font-medium text-sm">
                <Droplets className="h-4 w-4 mr-2" />
                Core Karnataka's First Doorstep Car Wash
              </div>
              
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0B2C5D] leading-tight">
                Premium Doorstep <br />
                <span className="text-[#1E88E5]">Car Wash</span> & <br />
                Auto Care
              </h1>
              
              <p className="text-xl text-[#0B2C5D] font-medium max-w-lg">
                We care for your car, while you save your time.
              </p>
              
              <div className="bg-[#0B2C5D] px-6 py-4 rounded-xl shadow-lg inline-block">
                <p className="font-heading font-bold text-lg text-white">
                  <span className="text-[#F7931E]">Washora</span> — Core Karnataka's First Doorstep Car Wash, beyond the capital.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/booking"
                  className="bg-[#F7931E] text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#E08215] transition-all transform hover:-translate-y-0.5 shadow-lg hover:shadow-orange-500/30 flex items-center justify-center"
                  data-testid="hero-book-now"
                >
                  Book Now <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/services"
                  className="bg-white text-[#0B2C5D] border-2 border-[#0B2C5D] px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#0B2C5D] hover:text-white transition-all flex items-center justify-center"
                  data-testid="hero-services"
                >
                  Our Services
                </Link>
              </div>
              
              <a
                href="https://wa.me/919044499957?text=Hi%20Washora,%20I'd%20like%20to%20book%20a%20car%20wash%20service."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-[#0B2C5D] hover:text-[#1E88E5] font-medium transition-colors"
                data-testid="hero-whatsapp"
              >
                <svg className="w-5 h-5 mr-2 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Or book via WhatsApp
              </a>
            </div>
            
            {/* Hero Image */}
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src={HERO_IMAGE} 
                  alt="Premium Luxury Car" 
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B2C5D]/30 to-transparent"></div>
              </div>
              
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6 max-w-[200px]">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-[#F7931E]/10 rounded-full flex items-center justify-center">
                    <Shield className="h-6 w-6 text-[#F7931E]" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-[#0B2C5D]">100%</p>
                    <p className="text-sm text-[#64748B]">Satisfaction</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {highlights.map((item, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 group cursor-pointer hover:border-blue-100 hover:-translate-y-1"
                data-testid={`highlight-${index}`}
              >
                <div className="w-14 h-14 bg-[#1E88E5]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#1E88E5] transition-colors">
                  <item.icon className="h-7 w-7 text-[#1E88E5] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-heading text-xl font-bold text-[#0B2C5D] mb-3">
                  {item.title}
                </h3>
                <p className="text-[#64748B]">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#0B2C5D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <p className="font-heading text-4xl md:text-5xl font-bold text-[#F7931E] mb-2">
                  {stat.value}
                </p>
                <p className="text-white/80">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B2C5D] mb-4">
              Our Premium Services
            </h2>
            <p className="text-[#64748B] max-w-2xl mx-auto">
              Professional car care services designed to keep your vehicle looking its best.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group hover:-translate-y-1"
                data-testid={`service-card-${index}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B2C5D]/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <span className="bg-[#F7931E] text-white px-4 py-1 rounded-full text-sm font-semibold">
                      {service.price}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-bold text-[#0B2C5D] mb-4">
                    {service.title}
                  </h3>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-[#64748B]">
                        <CheckCircle className="h-4 w-4 text-[#1E88E5] mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link
              to="/service-offer-plan"
              className="inline-flex items-center bg-[#F7931E] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#E08215] transition-all transform hover:-translate-y-0.5 shadow-lg hover:shadow-orange-500/30"
              data-testid="view-all-services"
            >
              View All Plans & Pricing <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Preview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B2C5D] mb-6">
                Why Choose Washora?
              </h2>
              <p className="text-[#64748B] mb-8">
                We're not just another car wash service. We're your trusted partner in vehicle care.
              </p>
              <div className="space-y-4">
                {[
                  'Doorstep Convenience – no travel, no waiting',
                  'Premium Quality – best products, trained team',
                  'Transparent Pricing – no hidden charges',
                  'Vehicle Value Protection – maintains car appearance',
                  'Customer-First Approach – responsive and personalized',
                ].map((item, index) => (
                  <div key={index} className="flex items-center">
                    <CheckCircle className="h-6 w-6 text-[#F7931E] mr-3 flex-shrink-0" />
                    <span className="text-[#0B2C5D]">{item}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/why-choose"
                className="inline-flex items-center mt-8 text-[#1E88E5] font-semibold hover:text-[#0B2C5D] transition-colors"
                data-testid="learn-more-why"
              >
                Learn More <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
            <div className="relative">
              <img 
                src={DETAILING_IMAGE}
                alt="Professional Car Detailing"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#1E88E5] to-[#0B2C5D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Experience Premium Care?
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto mb-8">
            Book your doorstep car wash service today and see why we're Karnataka's most trusted car care brand.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/booking"
              className="bg-[#F7931E] text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#E08215] transition-all transform hover:-translate-y-0.5 shadow-lg flex items-center justify-center"
              data-testid="cta-book-now"
            >
              Book Now <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <a
              href="tel:9044499957"
              className="bg-white text-[#0B2C5D] px-8 py-4 rounded-full font-semibold text-lg hover:bg-slate-100 transition-all flex items-center justify-center"
              data-testid="cta-call"
            >
              <Phone className="mr-2 h-5 w-5" />
              Call Us Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
